import React, { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { IMaskInput } from 'react-imask'
import IMask from 'imask'
import {
  ActivityTypes,
  ACTIVITY_TYPE_LABELS,
} from '../TrainingSessionsTable/utils'

// ─── Helpers ────────────────────────────────────────────────────────────────

const minSecToMs = (val: string): number | null => {
  const match = val.match(/^(\d{1,3}):(\d{2})$/)
  if (!match) return null
  return (parseInt(match[1], 10) * 60 + parseInt(match[2], 10)) * 1000
}

const hhmmssToMs = (val: string): number | null => {
  const match = val.match(/^(\d{1,3}):(\d{2}):(\d{2})$/)
  if (!match) return null
  return (
    (parseInt(match[1], 10) * 3600 +
      parseInt(match[2], 10) * 60 +
      parseInt(match[3], 10)) *
    1000
  )
}

const msToMinSec = (ms: number): string => {
  const totalSec = Math.round(ms / 1000)
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

const msToHHMMSS = (ms: number): string => {
  const totalSec = Math.round(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(
    s
  ).padStart(2, '0')}`
}

// ─── Distance presets ────────────────────────────────────────────────────────

const KM_OPTIONS = Array.from({ length: 42 }, (_, i) => {
  const meters = (i + 1) * 1000
  return {
    label: `${i + 1} km (${meters.toLocaleString()}m)`,
    meters,
    unit: 'km' as const,
  }
})

const MILE_OPTIONS = Array.from({ length: 26 }, (_, i) => {
  const meters = Math.round((i + 1) * 1609.344)
  return {
    label: `${i + 1} mile${i > 0 ? 's' : ''} (${meters.toLocaleString()}m)`,
    meters,
    unit: 'mi' as const,
  }
})

function getDistanceOptions(unitSystem?: string | null) {
  if (unitSystem === 'imperial') return MILE_OPTIONS
  if (unitSystem === 'metric') return KM_OPTIONS
  return [...KM_OPTIONS, ...MILE_OPTIONS].sort((a, b) => a.meters - b.meters)
}

// ─── Types ───────────────────────────────────────────────────────────────────

type SessionFormData = {
  title: string
  activityType: string
  date: string
}

export type AddSessionData = SessionFormData & {
  sportType: string
  goalDistanceMeters: number | null
  goalPaceMsPerKm: number | null
  goalTimeMs: number | null
}

type AddSessionModalProps = {
  onAdd: (data: AddSessionData) => void
  onClose: () => void
  minDate?: string
  maxDate?: string
  unitSystem?: string | null
}

const activityTypeOptions = Object.values(ActivityTypes).filter(
  (t) => t !== ActivityTypes.strengthVideo && t !== ActivityTypes.strengthGifs
)

// ─── Component ───────────────────────────────────────────────────────────────

export default function AddSessionModal({
  onAdd,
  onClose,
  minDate,
  maxDate,
  unitSystem,
}: AddSessionModalProps) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<SessionFormData>({ mode: 'onChange' })

  const [selectedDistanceMeters, setSelectedDistanceMeters] = useState<
    number | null
  >(null)
  const [paceInput, setPaceInput] = useState('')
  const [durationInput, setDurationInput] = useState('')

  const parsedPace = useMemo(() => {
    if (!/^\d{1,3}:\d{2}$/.test(paceInput)) return null
    return minSecToMs(paceInput)
  }, [paceInput])

  const parsedDuration = useMemo(() => {
    if (!/^\d{1,3}:\d{2}:\d{2}$/.test(durationInput)) return null
    return hhmmssToMs(durationInput)
  }, [durationInput])

  const hasDistance =
    selectedDistanceMeters !== null && selectedDistanceMeters > 0
  const hasTime = parsedDuration !== null && parsedDuration > 0
  const hasPace = parsedPace !== null && parsedPace > 0

  const distanceOrTimeValid =
    (hasDistance && !hasTime) || (!hasDistance && hasTime)
  const measurementsValid = hasPace && distanceOrTimeValid

  const onSubmit = (formData: SessionFormData) => {
    onAdd({
      ...formData,
      sportType: 'Running',
      goalDistanceMeters: hasDistance ? selectedDistanceMeters : null,
      goalPaceMsPerKm: parsedPace ?? null,
      goalTimeMs: hasTime ? parsedDuration : null,
    })
  }

  const inputCls = (hasError?: boolean) =>
    `w-full border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${
      hasError ? 'border-red-400' : 'border-gray-300'
    }`

  const roInputCls =
    'w-full border border-gray-200 rounded px-3 py-1.5 text-sm bg-gray-50 text-gray-500 cursor-not-allowed'

  const labelCls = 'w-36 text-sm text-gray-600 text-right shrink-0 pt-1.5'

  const distanceConflict = hasDistance && hasTime
  const distanceMissing = !hasDistance && !hasTime

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black bg-opacity-40"
        role="presentation"
        onClick={onClose}
        onKeyDown={onClose}
      />
      <div className="relative z-10 bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Add session</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl font-bold"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="space-y-3">
            {/* Session name */}
            <div className="flex items-start gap-3">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="title" className={labelCls}>
                Session name
              </label>
              <div className="flex-1">
                <input
                  id="title"
                  type="text"
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...register('title', { required: 'Title is required' })}
                  className={inputCls(!!errors.title)}
                />
                {errors.title && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.title.message}
                  </p>
                )}
              </div>
            </div>

            {/* Activity type */}
            <div className="flex items-start gap-3">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="activityType" className={labelCls}>
                Activity type
              </label>
              <div className="flex-1">
                <select
                  id="activityType"
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...register('activityType', {
                    required: 'Activity type is required',
                  })}
                  className={`${inputCls(!!errors.activityType)} bg-white`}
                >
                  <option value="">Select type</option>
                  {activityTypeOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {ACTIVITY_TYPE_LABELS[opt] ?? opt}
                    </option>
                  ))}
                </select>
                {errors.activityType && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.activityType.message}
                  </p>
                )}
              </div>
            </div>

            {/* Date */}
            <div className="flex items-start gap-3">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="date" className={labelCls}>
                Date
              </label>
              <div className="flex-1">
                <input
                  id="date"
                  type="date"
                  min={minDate}
                  max={maxDate}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...register('date', { required: 'Date is required' })}
                  className={inputCls(!!errors.date)}
                />
                {errors.date && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.date.message}
                  </p>
                )}
              </div>
            </div>

            <div className="border-t border-gray-100 pt-3 mt-1">
              <p className="text-xs text-gray-400 mb-2 text-right">
                Required: Goal pace, and either Distance selection or Goal time
              </p>

              {/* Goal time */}
              <div className="flex items-start gap-3">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="duration" className={labelCls}>
                  Goal time (hh:mm:ss)
                </label>
                <div className="flex-1">
                  <IMaskInput
                    id="duration"
                    mask="HH:MM:SS"
                    blocks={{
                      HH: {
                        mask: IMask.MaskedRange,
                        from: 0,
                        to: 99,
                        maxLength: 2,
                      },
                      MM: {
                        mask: IMask.MaskedRange,
                        from: 0,
                        to: 59,
                        maxLength: 2,
                      },
                      SS: {
                        mask: IMask.MaskedRange,
                        from: 0,
                        to: 59,
                        maxLength: 2,
                      },
                    }}
                    lazy={false}
                    value={durationInput}
                    onAccept={(val: string) => setDurationInput(val)}
                    className={inputCls(distanceConflict || distanceMissing)}
                  />
                  {(distanceConflict || distanceMissing) && (
                    <p className="mt-1 text-xs text-red-500">
                      Pick exactly one: Goal time or Distance selection
                    </p>
                  )}
                </div>
              </div>

              {/* Distance selection */}
              <div className="flex items-start gap-3 mt-3">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="distance" className={labelCls}>
                  Distance selection
                </label>
                <div className="flex-1">
                  <div className="flex items-center gap-1">
                    <select
                      id="distance"
                      value={selectedDistanceMeters ?? ''}
                      onChange={(e) =>
                        setSelectedDistanceMeters(
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                      className={`${inputCls(
                        distanceConflict || distanceMissing
                      )} bg-white`}
                    >
                      <option value="">— select —</option>
                      {getDistanceOptions(unitSystem).map((opt) => (
                        <option
                          key={`${opt.unit}-${opt.meters}`}
                          value={opt.meters}
                        >
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {selectedDistanceMeters != null &&
                      selectedDistanceMeters > 0 && (
                        <button
                          type="button"
                          onClick={() => setSelectedDistanceMeters(null)}
                          className="px-1 text-red-500 hover:text-red-700 text-sm leading-none"
                          title="Clear distance selection"
                          aria-label="Clear distance selection"
                        >
                          ×
                        </button>
                      )}
                  </div>
                  {(distanceConflict || distanceMissing) && (
                    <p className="mt-1 text-xs text-red-500">
                      Pick exactly one: Goal time or Distance selection
                    </p>
                  )}
                </div>
              </div>

              {/* Goal distance (read-only metres) */}
              <div className="flex items-start gap-3 mt-3">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className={labelCls}>Goal distance (metres)</label>
                <div className="flex-1">
                  <input
                    type="text"
                    readOnly
                    value={
                      selectedDistanceMeters != null
                        ? `${selectedDistanceMeters.toLocaleString()} m`
                        : ''
                    }
                    placeholder="—"
                    className={roInputCls}
                  />
                </div>
              </div>

              {/* Goal pace */}
              <div className="flex items-start gap-3 mt-3">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="pace" className={labelCls}>
                  Goal pace (min/km)
                </label>
                <div className="flex-1">
                  <IMaskInput
                    id="pace"
                    mask="MM:SS"
                    blocks={{
                      MM: {
                        mask: IMask.MaskedRange,
                        from: 0,
                        to: 99,
                        maxLength: 2,
                      },
                      SS: {
                        mask: IMask.MaskedRange,
                        from: 0,
                        to: 59,
                        maxLength: 2,
                      },
                    }}
                    lazy={false}
                    value={paceInput}
                    onAccept={(val: string) => setPaceInput(val)}
                    className={inputCls(!hasPace)}
                  />
                  {!hasPace && (
                    <p className="mt-1 text-xs text-red-500">
                      Goal pace is required
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="submit"
              disabled={!isValid || !measurementsValid}
              className="flex-1 py-2 text-sm font-medium rounded-lg bg-green-500 text-white hover:bg-green-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Add session
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 text-sm font-medium rounded-lg bg-red-400 text-white hover:bg-red-500 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
